import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Scissors,
  Clock,
  DollarSign,
  CheckCircle,
  TrendingUp,
  Loader2,
  Plus,
  ArrowRight,
  Sparkles,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';

const APPLY_DISMISS_KEY = 'creator_apply_dismissed';
const SOCIAL_FIELDS = [
  { key: 'instagram', label: 'Instagram', placeholder: '@handle or URL' },
  { key: 'tiktok', label: 'TikTok', placeholder: '@handle or URL' },
  { key: 'youtube', label: 'YouTube', placeholder: '@handle or URL' },
  { key: 'facebook', label: 'Facebook', placeholder: 'Page name or URL' },
];

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentCosplays, setRecentCosplays] = useState([]);
  const [loading, setLoading] = useState(true);

  // Creator Portal application
  const [applicationStatus, setApplicationStatus] = useState(undefined); // undefined = unknown, null = none
  const [applyDismissed, setApplyDismissed] = useState(() => localStorage.getItem(APPLY_DISMISS_KEY) === '1');
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyName, setApplyName] = useState('');
  const [applySocials, setApplySocials] = useState({ instagram: '', tiktok: '', youtube: '', facebook: '' });
  const [applySubmitting, setApplySubmitting] = useState(false);

  useEffect(() => {
    if (!user || user.creator_role) return;
    api.get('/creator-application/me')
      .then((r) => setApplicationStatus(r.data?.status ?? null))
      .catch(() => setApplicationStatus(null));
  }, [user]);

  useEffect(() => {
    if (user?.display_name) setApplyName(user.display_name);
  }, [user]);

  // Show the banner only to non-creators with no pending/approved application, not dismissed.
  const showApplyBanner =
    !!user &&
    !user.creator_role &&
    !applyDismissed &&
    (applicationStatus === null || applicationStatus === 'denied');

  const dismissApply = () => {
    localStorage.setItem(APPLY_DISMISS_KEY, '1');
    setApplyDismissed(true);
  };

  const submitApplication = async () => {
    const name = applyName.trim();
    const hasSocial = SOCIAL_FIELDS.some((f) => applySocials[f.key].trim());
    if (!name) { toast.error('Please enter your name.'); return; }
    if (!hasSocial) { toast.error('Add at least one social link.'); return; }
    setApplySubmitting(true);
    try {
      await api.post('/creator-application', { name, ...applySocials });
      toast.success("Application submitted — we'll be in touch!");
      setApplyOpen(false);
      setApplicationStatus('pending');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not submit your application. Please try again.');
    } finally {
      setApplySubmitting(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, cosplaysRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/cosplays')
      ]);
      setStats(statsRes.data);
      // Handle both array and object responses
      const cosplaysData = Array.isArray(cosplaysRes.data) 
        ? cosplaysRes.data 
        : (cosplaysRes.data?.cosplays || []);
      setRecentCosplays(cosplaysData.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setRecentCosplays([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Cosplays', value: stats?.total_cosplays || 0, icon: Scissors },
    { label: 'In Progress', value: stats?.in_progress || 0, icon: TrendingUp },
    { label: 'Completed', value: stats?.completed || 0, icon: CheckCircle },
    { label: 'Total Spent', value: `$${(stats?.total_spent || 0).toLocaleString()}`, icon: DollarSign, mono: true },
    { label: 'Hours Logged', value: `${(stats?.total_hours || 0).toLocaleString()}h`, icon: Clock, mono: true },
  ];

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'status-completed';
      case 'in_progress': return 'status-in_progress';
      default: return 'status-planning';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      default: return 'Planning';
    }
  };

  return (
    <div className="space-y-8 animate-fadeIn" data-testid="dashboard-page">
      {/* Creator Portal apply banner */}
      {showApplyBanner && (
        <div
          className="relative overflow-hidden win-raised p-4 sm:p-5"
          data-testid="creator-apply-banner"
        >
          <button
            onClick={dismissApply}
            aria-label="Dismiss"
            className="absolute top-2 right-2 p-1"
            data-testid="dismiss-creator-apply"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 pr-6">
            <div className="w-10 h-10 win-well flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">Become a Creator</p>
              <p className="text-sm">Apply for the Creator role to unlock your own links page and the Create tab.</p>
            </div>
            <Button
              onClick={() => setApplyOpen(true)}
              className="font-semibold flex-shrink-0"
              data-testid="open-creator-apply"
            >
              Apply
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Dashboard</h1>
          <p className="mt-1">Track your cosplay journey</p>
        </div>
        <Link to="/cosplays">
          <Button
            className="font-semibold gap-2"
            data-testid="new-cosplay-btn"
          >
            <Plus className="w-5 h-5" />
            New Cosplay
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {statCards.map((stat, index) => (
          <div
            key={stat.label}
            className="win-raised p-6"
            style={{ animationDelay: `${index * 50}ms` }}
            data-testid={`stat-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon className="w-6 h-6" />
            </div>
            <p className={`text-2xl font-bold ${stat.mono ? 'font-mono' : ''}`}>
              {stat.value}
            </p>
            <p className="text-sm mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Cosplays */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-semibold">Recent Cosplays</h2>
          <Link
            to="/cosplays"
            className="text-sm font-medium flex items-center gap-1"
            data-testid="view-all-cosplays-link"
          >
            View All
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {recentCosplays.length === 0 ? (
          <div className="win-well p-12 text-center">
            <Scissors className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-lg font-heading font-semibold mb-2">No Cosplays Yet</h3>
            <p className="mb-6">Start your first cosplay project!</p>
            <Link to="/cosplays">
              <Button className="font-semibold gap-2">
                <Plus className="w-5 h-5" />
                Create Cosplay
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentCosplays.map((cosplay) => (
              <Link
                key={cosplay.id}
                to={`/cosplays/${cosplay.id}`}
                className="win-raised p-6 overflow-hidden group"
                data-testid={`cosplay-card-${cosplay.id}`}
              >
                <div className="flex gap-4">
                  {/* Cover Image */}
                  <div className="w-20 h-20 win-well flex-shrink-0 overflow-hidden">
                    {cosplay.cover_image ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL || ''}${cosplay.cover_image}`}
                        alt={cosplay.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Scissors className="w-8 h-8" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-heading font-semibold truncate">
                          {cosplay.title}
                        </h3>
                        <p className="text-sm truncate">{cosplay.character_name}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium flex-shrink-0 whitespace-nowrap ${getStatusClass(cosplay.status)}`}>
                        {getStatusLabel(cosplay.status)}
                      </span>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span className="font-mono">{cosplay.progress}%</span>
                      </div>
                      <Progress value={cosplay.progress} className="h-2" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Creator Portal application form */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Apply to be a Creator
            </DialogTitle>
            <DialogDescription>
              Tell us who you are and where to find you. Add at least one social link. We&apos;ll review and get back to you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Your name</Label>
              <Input
                value={applyName}
                onChange={(e) => setApplyName(e.target.value)}
                placeholder="Your name"
                className="mt-1"
                data-testid="apply-name-input"
              />
            </div>
            <div className="space-y-3">
              <p className="text-xs">Add at least one of the following:</p>
              {SOCIAL_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label>{f.label}</Label>
                  <Input
                    value={applySocials[f.key]}
                    onChange={(e) => setApplySocials((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder}
                    className="mt-1"
                    data-testid={`apply-${f.key}-input`}
                  />
                </div>
              ))}
            </div>
            <Button
              onClick={submitApplication}
              disabled={applySubmitting}
              className="w-full font-semibold"
              data-testid="submit-creator-apply"
            >
              {applySubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Application'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardPage;
